import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen, Users, Shield, Calendar, PoundSterling, Music, FileText,
  Clock, Mail, BarChart3, Smartphone, ChevronDown, ChevronRight,
  Home, Globe, UserCheck, QrCode, Printer, ThumbsUp, ThumbsDown,
  ArrowUp, ArrowDown, Download, CheckCircle2, Eye, Play, Pause
} from 'lucide-react';
import ScreenshotStudio from '../components/admin/ScreenshotStudio';

const PRODUCTION_URL = 'https://cccvob.com';
const CHURCH_NAME = 'CCC Valley of Berachah (VOB) Parish';
const CHURCH_NAME_FULL = 'Celestial Church of Christ — Valley of Berachah (VOB) Parish';
const BUILT_BY = 'Oluwakorede Akinsanya';
const HANDBOOK_VERSION = 'v1.1';
const HANDBOOK_UPDATED = 'February 2026';

// Self-hosted screenshots captured from the actual VOB admin/public pages.
// Located at /app/frontend/public/handbook/*.jpg — no external CDN required.
const IMG = {
  church: '/handbook/church.jpg',
  register: '/handbook/register.jpg',
  dashboard: '/handbook/dashboard.jpg',
  rota: '/handbook/rota.jpg',
  finance: '/handbook/finance.jpg',
  choir: '/handbook/choir.jpg',
  volunteer: '/handbook/volunteer.jpg',
  bible: '/handbook/bible.jpg',
  email: '/handbook/email.jpg',
  mobile: '/handbook/mobile.jpg',
  team: '/handbook/team.jpg',
  worship: '/handbook/worship.jpg',
  receipt: '/handbook/receipt.jpg',
  planning: '/handbook/planning.jpg',
  music: '/handbook/music.jpg',
  pres: '/handbook/pres.jpg',
  qr: '/handbook/qr.jpg',
};

const SectionImg = ({ src, alt, caption, fallbackIcon: FallbackIcon = Globe, fallbackGradient = 'from-violet-500 to-purple-700' }) => {
  const [errored, setErrored] = React.useState(false);
  return (
    <figure className="my-6 rounded-2xl overflow-hidden shadow-lg border border-slate-200">
      {errored ? (
        <div className={`w-full h-48 sm:h-64 flex items-center justify-center bg-gradient-to-br ${fallbackGradient}`}>
          <FallbackIcon className="w-20 h-20 text-white/80" strokeWidth={1.2} />
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          onError={() => setErrored(true)}
          className="w-full h-48 sm:h-64 object-cover"
          loading="lazy"
        />
      )}
      {caption && <figcaption className="px-4 py-2 bg-slate-50 text-sm text-slate-500 text-center">{caption}</figcaption>}
    </figure>
  );
};

const Step = ({ number, title, children }) => (
  <div className="flex gap-4 mb-4">
    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold shrink-0 mt-0.5">{number}</div>
    <div className="flex-1">
      <h4 className="font-bold text-slate-800 mb-1">{title}</h4>
      <p className="text-slate-600 text-sm leading-relaxed">{children}</p>
    </div>
  </div>
);

const InfoBox = ({ type = 'info', children }) => {
  const styles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    tip: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    url: 'bg-indigo-50 border-indigo-200 text-indigo-800',
  };
  return <div className={`p-4 rounded-xl border text-sm leading-relaxed my-4 ${styles[type]}`}>{children}</div>;
};

const TOC_SECTIONS = [
  { id: 'overview', label: 'Platform Overview', icon: Globe },
  { id: 'whats-new', label: "What's New (v1.1)", icon: ThumbsUp },
  { id: 'homepage', label: 'Church Homepage', icon: Home },
  { id: 'registration', label: 'Member Registration', icon: UserCheck },
  { id: 'member-login', label: 'Member Password Login', icon: Shield },
  { id: 'admin', label: 'Admin Panel & Login', icon: Shield },
  { id: 'members', label: 'Members Management', icon: Users },
  { id: 'rota', label: 'Rota Management', icon: Calendar },
  { id: 'availability', label: 'Minister Availability', icon: Calendar },
  { id: 'harvest', label: 'Harvest & Finance', icon: PoundSterling },
  { id: 'choir', label: 'Choir Ministry', icon: Music },
  { id: 'choir-checkin', label: 'Choir Check-in (GPS/QR)', icon: QrCode },
  { id: 'volunteers', label: 'Volunteer Tracking', icon: Clock },
  { id: 'rules', label: 'Rules & Tenets', icon: BookOpen },
  { id: 'email', label: 'Communications', icon: Mail },
  { id: 'analytics', label: 'Analytics & Reports', icon: BarChart3 },
  { id: 'mobile', label: 'Mobile Access & QR Codes', icon: Smartphone },
  { id: 'urls', label: 'Quick Reference URLs', icon: Globe },
];

const Handbook = ({ embedded = false }) => {
  const [tocOpen, setTocOpen] = useState(true);
  const [downloading, setDownloading] = useState(false);
  // Auto-version pill — fetched from backend (reads /app/memory/PRD.md on each call)
  // Falls back to the hard-coded constants if the backend is unreachable.
  const [versionInfo, setVersionInfo] = useState({
    version: HANDBOOK_VERSION,
    updated: HANDBOOK_UPDATED,
  });
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backend}/api/handbook/version`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && d.version) setVersionInfo({ version: d.version, updated: d.updated }); })
      .catch(() => {});
  }, []);

  // Show the download button to ANY logged-in admin. The backend enforces
  // the actual permission (Main Admin or Secretariat) and returns 401/403
  // with friendly alerts below if the user isn't authorised.
  const adminToken = (() => {
    if (typeof window === 'undefined') return '';
    // Cover every token key that has ever been used by the app.
    const candidates = ['adminToken', 'admin_token', 'v2.1.0.church.adminToken'];
    for (const k of candidates) {
      const v = localStorage.getItem(k);
      if (v) return v;
    }
    // Fallback: any localStorage key whose name looks like an admin token.
    for (const k of Object.keys(localStorage)) {
      if (/admin.?token/i.test(k)) {
        const v = localStorage.getItem(k);
        if (v) return v;
      }
    }
    return '';
  })();
  const canDownload = !!adminToken;

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${backend}/api/handbook/download`, {
        headers: { 'x-admin-token': adminToken },
      });
      if (res.status === 401) {
        alert('Admin sign-in required to download the Handbook.');
        return;
      }
      if (res.status === 403) {
        alert('Only Main Admin or Secretariat can download the Handbook.');
        return;
      }
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'CCC-VOB-Parish-Handbook.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to download handbook. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 handbook-root" data-testid="handbook-page">
      {/* Print CSS - applies only when printing/saving as PDF */}
      <style>{`
        @media print {
          @page { size: A4; margin: 16mm 14mm; }
          body { background: white !important; }
          .handbook-noprint { display: none !important; }
          .handbook-root { background: white !important; }
          header { page-break-after: avoid; }
          section { page-break-inside: avoid; }
          img { max-width: 100% !important; height: auto !important; }
          .handbook-toc { display: none !important; }
          a { color: #1e293b !important; text-decoration: none !important; }
          footer { page-break-before: avoid; }
        }
      `}</style>
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 text-white relative">
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 text-sm font-medium mb-4 backdrop-blur border border-white/20">
            <BookOpen className="w-4 h-4" /> Official Handbook
          </div>
          <div
            className="handbook-noprint inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/25 border border-amber-300/40 text-amber-100 text-xs font-semibold mb-4 ml-2 backdrop-blur"
            data-testid="handbook-version-pill"
            title="Auto-updates from PRD.md every time a new CHANGELOG entry is committed"
          >
            <CheckCircle2 className="w-3 h-3" /> {versionInfo.version} · updated {versionInfo.updated}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-3">{CHURCH_NAME}</h1>
          <p className="text-lg text-indigo-200">Church Management System &amp; Website — User Guide &amp; Handbook</p>
          <p className="text-sm text-indigo-300 mt-2">{versionInfo.version} — {versionInfo.updated}</p>
          {canDownload && (
            <div className="handbook-noprint mt-6 flex flex-col items-center sm:items-start">
              {/* Preview thumbnail strip — signals the PDF now has real screenshots */}
              <div
                className="flex items-center -space-x-2 mb-2"
                data-testid="handbook-download-preview"
                aria-hidden="true"
                title="Real screenshots included in the PDF"
              >
                {['dashboard', 'rota', 'choir', 'finance'].map((name) => (
                  <img
                    key={name}
                    src={`/handbook/${name}.jpg`}
                    alt=""
                    className="w-9 h-9 rounded-lg object-cover ring-2 ring-white/40 shadow-md hover:scale-110 transition-transform"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ))}
                <span className="ml-3 text-[11px] font-medium text-indigo-200/90 uppercase tracking-wider">
                  Now with real screenshots
                </span>
              </div>
              <button
                onClick={handleDownload}
                disabled={downloading}
                data-testid="handbook-download-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-indigo-700 rounded-full font-semibold text-sm hover:bg-indigo-50 transition-colors shadow-lg disabled:opacity-60 disabled:cursor-wait"
              >
                <Download className="w-4 h-4" /> {downloading ? 'Preparing PDF…' : 'Download Handbook (PDF)'}
              </button>
              <ScreenshotStudio />
            </div>
          )}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8 flex gap-8">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block w-64 shrink-0 handbook-toc">
          <div className="sticky top-6">
            <nav className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
              <h3 className="font-bold text-slate-800 text-sm mb-3 px-2">Contents</h3>
              <ul className="space-y-0.5">
                {TOC_SECTIONS.map(s => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors">
                      <s.icon className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{s.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="mt-4 bg-indigo-50 rounded-xl p-3 border border-indigo-100 text-center">
              <a href="/platform-presentation" className="text-sm text-indigo-700 font-medium hover:underline flex items-center justify-center gap-1">
                <Play className="w-3.5 h-3.5" /> View Presentation Slides
              </a>
            </div>
          </div>
        </aside>

        {/* Mobile TOC */}
        <div className="lg:hidden fixed bottom-4 right-4 z-50 handbook-noprint">
          <button onClick={() => setTocOpen(!tocOpen)} className="w-12 h-12 rounded-full bg-indigo-600 text-white shadow-lg flex items-center justify-center">
            <BookOpen className="w-5 h-5" />
          </button>
          {tocOpen && (
            <div className="absolute bottom-14 right-0 bg-white rounded-2xl p-4 shadow-xl border border-slate-200 w-64 max-h-96 overflow-y-auto">
              <h3 className="font-bold text-sm mb-2">Contents</h3>
              <ul className="space-y-1">
                {TOC_SECTIONS.map(s => (
                  <li key={s.id}><a href={`#${s.id}`} onClick={() => setTocOpen(false)} className="block py-1 text-sm text-slate-600 hover:text-indigo-600">{s.label}</a></li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          {/* Section: Overview */}
          <section id="overview" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <Globe className="w-7 h-7 text-indigo-600" /> Platform Overview
            </h2>
            <SectionImg src={IMG.church} alt={CHURCH_NAME} caption={`${CHURCH_NAME} — Church Management System & Website`} fallbackIcon={Globe} fallbackGradient="from-violet-600 to-purple-800" />
            <p className="text-slate-700 leading-relaxed mb-4">
              The {CHURCH_NAME} Church Management System &amp; Website (cccvob.com) is a comprehensive, full-stack digital platform built to streamline every aspect of church administration and provide a public-facing church website. From online member registration to harvest finance, rota scheduling to choir management, Bible lessons to volunteer tracking — everything is accessible from one centralised system, on desktop and mobile.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {[
                { icon: Users, label: 'Member Management', desc: 'Register, track, and manage church members' },
                { icon: Calendar, label: 'Rota Generation', desc: 'Monthly service rotas with Bible lessons' },
                { icon: PoundSterling, label: 'Harvest & Finance', desc: 'Contributions, receipts, and disbursements' },
                { icon: Music, label: 'Choir Ministry', desc: 'Members, songs, hymns, and applications' },
                { icon: Clock, label: 'Volunteer Tracking', desc: 'QR clock-in/out with hour tracking' },
                { icon: BookOpen, label: 'Rules & Tenets', desc: 'Full-screen presentation slides' },
                { icon: Mail, label: 'Communications', desc: 'Branded email notifications' },
                { icon: BarChart3, label: 'Analytics', desc: 'Attendance trends and financial reports' },
              ].map(f => (
                <div key={f.label} className="flex items-start gap-3 p-3 bg-white rounded-xl border border-slate-100">
                  <div className="p-2 rounded-lg bg-indigo-50"><f.icon className="w-4 h-4 text-indigo-600" /></div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{f.label}</p>
                    <p className="text-xs text-slate-500">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section: What's New */}
          <section id="whats-new" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <ThumbsUp className="w-7 h-7 text-indigo-600" /> What's New — Version 1.1
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Since Version 1.0, the platform has grown with several major features. Every item below is already live on <span className="font-mono text-xs bg-slate-100 px-1 rounded">{PRODUCTION_URL}</span>.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {[
                { label: 'Minister Availability Calendar', desc: 'Ministers mark themselves unavailable via /minister-availability (7-day lockout). Rota UI flags unavailable ministers in red.' },
                { label: 'Hybrid Choir Check-in (GPS + QR)', desc: 'Choir members sign in via /choir-checkin. Direct links require GPS inside the church (100m); scanning the on-site QR with a venue token bypasses GPS.' },
                { label: 'Member Password Login', desc: 'Members set their own password via an emailed link. Choir members now log in with Phone + Password instead of Phone + Surname.' },
                { label: 'Pending Password Setup Widget', desc: 'Admin Dashboard shows a live count of members who have not completed password setup, with a one-click "Resend All" action.' },
                { label: 'Rota Email Automation', desc: '"Email Ministers" sends full rota + personal assignment emails. Automatic 3-day reminders include an "Update My Availability" link.' },
                { label: 'A3 Rota PDFs with Auto-Scaling', desc: 'Huge monthly rotas always fit on a single A3 page — fonts and padding scale automatically.' },
                { label: 'Drag-and-Drop Rules Ordering', desc: 'Rules & Tenets sections are now reordered by dragging, not up/down arrows. Order is reflected instantly on the presentation.' },
                { label: 'Split Weekly Schedule Slides', desc: 'Each service day now has its own full-screen slide in the Rules presentation — no crammed grids.' },
                { label: 'Compulsory Choir Form Fields', desc: '/join-choir enforces every required field with visual asterisks and server-side validation — no partial applications.' },
                { label: 'Main-Admin-Only Handbook Download', desc: 'The "Download Handbook (PDF)" button is visible only to the Main Admin. The /api/handbook/download endpoint is server-side locked too (403 for anyone else).' },
              ].map(i => (
                <div key={i.label} className="p-3 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                  <p className="font-semibold text-indigo-900 text-sm flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> {i.label}</p>
                  <p className="text-xs text-indigo-800/80 mt-1 leading-relaxed">{i.desc}</p>
                </div>
              ))}
            </div>
            <InfoBox type="tip">
              <strong>Ongoing policy:</strong> Every time a new feature ships to cccvob.com, the corresponding chapter below (and the downloadable PDF) is updated. If something on the live site is not yet documented here, tell the admin — the handbook is meant to be a living document.
            </InfoBox>
          </section>

          {/* Section: Homepage */}
          <section id="homepage" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <Home className="w-7 h-7 text-indigo-600" /> Church Homepage
            </h2>
            <SectionImg src={IMG.worship} alt="Church Homepage" caption="The public homepage at cccvob.com" fallbackIcon={Home} fallbackGradient="from-blue-500 to-indigo-700" />
            <InfoBox type="url"><strong>URL:</strong> <span className="font-mono">{PRODUCTION_URL}</span></InfoBox>
            <p className="text-slate-700 leading-relaxed mb-4">
              The church homepage is the public face of VOB online. It is fully mobile-responsive and accessible to anyone without login.
            </p>
            <h3 className="font-bold text-slate-800 text-lg mb-2">What visitors see:</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1 mb-4 text-sm">
              <li><strong>Church announcements</strong> — Latest news and updates from leadership</li>
              <li><strong>Service schedules</strong> — Upcoming Sunday and midweek service times</li>
              <li><strong>Ministries</strong> — Active church ministries with descriptions</li>
              <li><strong>Inspirational flyers</strong> — Visual flyer gallery</li>
              <li><strong>Member registration form</strong> — Join the church online (see next section)</li>
              <li><strong>About section</strong> — Church history and mission statement</li>
            </ul>
          </section>

          {/* Section: Registration */}
          <section id="registration" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <UserCheck className="w-7 h-7 text-indigo-600" /> Member Registration
            </h2>
            <SectionImg src={IMG.register} alt="Member Registration" caption="Members can register via homepage form or QR code" fallbackIcon={UserCheck} fallbackGradient="from-emerald-500 to-teal-700" />
            <p className="text-slate-700 leading-relaxed mb-4">
              There are two ways for new members to register:
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <h4 className="font-bold text-slate-800 flex items-center gap-2 mb-2"><Globe className="w-4 h-4 text-blue-500" /> Homepage Form</h4>
                <p className="text-sm text-slate-600 mb-2">Available on the homepage at <span className="font-mono text-xs">{PRODUCTION_URL}</span></p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>- Click "Register as Member"</li>
                  <li>- Fill in all mandatory fields</li>
                  <li>- Submission goes to admin for approval</li>
                  <li>- Status: Pending until accepted</li>
                </ul>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <h4 className="font-bold text-slate-800 flex items-center gap-2 mb-2"><QrCode className="w-4 h-4 text-emerald-500" /> QR Code Registration</h4>
                <p className="text-sm text-slate-600 mb-2">Scan QR at <span className="font-mono text-xs">{PRODUCTION_URL}/member-register</span></p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>- Scan QR code displayed at church entrance</li>
                  <li>- Fill in registration form on phone</li>
                  <li>- GPS location captured automatically</li>
                  <li>- Status: Automatically accepted</li>
                </ul>
              </div>
            </div>
            <h3 className="font-bold text-slate-800 text-lg mb-2">Required Fields (All Mandatory):</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              {['Full Name', 'Phone Number (UK)', 'Email', 'Birthday (Month + Day)', 'Gender'].map(f => (
                <div key={f} className="px-3 py-2 bg-indigo-50 rounded-lg text-sm font-medium text-indigo-700 text-center">{f}</div>
              ))}
            </div>
            <InfoBox type="tip"><strong>Confirmation:</strong> Homepage registrations receive the message: <em>"God bless you for joining us. We are glad to have you."</em></InfoBox>
          </section>

          {/* Section: Member Password Login */}
          <section id="member-login" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <Shield className="w-7 h-7 text-indigo-600" /> Member Password Login
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Once a homepage registration is accepted, the member is emailed a secure link to set their own password. After setup, they sign in to member-facing features (e.g. Choir Check-in) using <strong>Phone Number + Password</strong> — no more "phone + surname" guessing.
            </p>
            <h3 className="font-bold text-slate-800 text-lg mb-2">How the flow works:</h3>
            <Step number="1" title="Admin accepts registration">The member receives a branded "Set your password" email with a one-time token link.</Step>
            <Step number="2" title="Member sets password">They click the link, choose a password (min 6 characters), and the token is marked consumed.</Step>
            <Step number="3" title="Member logs in">From the homepage Choir Member Login (or any future member area) they enter Phone + Password.</Step>
            <Step number="4" title="Idempotent resends">If an admin re-triggers password setup in bulk (e.g. "Enroll ALL"), existing valid tokens are reused — members' emailed links stay valid.</Step>
            <h3 className="font-bold text-slate-800 text-lg mb-2 mt-4">Pending Password Setup Widget (Admin):</h3>
            <p className="text-sm text-slate-600 mb-2">The Admin Analytics Dashboard shows a live <strong>Pending Password Setup</strong> card listing every member who hasn't completed setup. One click re-sends all setup emails.</p>
            <InfoBox type="info"><strong>Choir Password Status tab:</strong> Choir Ministry → Password Status shows real-time metrics: total enrolled, pending, overdue, and last-login timestamps per member.</InfoBox>
          </section>

          {/* Section: Admin */}
          <section id="admin" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <Shield className="w-7 h-7 text-indigo-600" /> Admin Panel & Login
            </h2>
            <SectionImg src={IMG.dashboard} alt="Admin Dashboard" caption="The admin panel with analytics and module access" fallbackIcon={Shield} fallbackGradient="from-slate-700 to-slate-900" />
            <InfoBox type="url"><strong>Login URL:</strong> <span className="font-mono">{PRODUCTION_URL}/login</span></InfoBox>
            <h3 className="font-bold text-slate-800 text-lg mb-2">How to Log In:</h3>
            <Step number="1" title="Navigate to Login">Go to <span className="font-mono text-xs bg-slate-100 px-1 rounded">{PRODUCTION_URL}/login</span> in your browser.</Step>
            <Step number="2" title="Enter Credentials">Type your admin username and password, then click <strong>"Sign In"</strong>.</Step>
            <Step number="3" title="Access Admin Panel">Once logged in, click the <strong>"Admin"</strong> tab in the bottom navigation bar to access the full admin panel.</Step>
            <h3 className="font-bold text-slate-800 text-lg mb-2 mt-6">User Roles:</h3>
            <div className="space-y-2 mb-4">
              {[
                { role: 'Main Admin', desc: 'Full access to all modules. Can create/delete other admins, void contributions, and manage all settings.', color: 'bg-red-100 text-red-700' },
                { role: 'Sub-Admin', desc: 'Access to specific modules based on assigned permissions (e.g. Finance only, Rota only).', color: 'bg-blue-100 text-blue-700' },
                { role: 'Secretariat', desc: 'Access to member management, attendance records, and reporting.', color: 'bg-emerald-100 text-emerald-700' },
              ].map(r => (
                <div key={r.role} className="flex items-start gap-3 p-3 bg-white rounded-xl border border-slate-100">
                  <span className={`px-2 py-1 rounded-lg text-xs font-bold ${r.color} shrink-0`}>{r.role}</span>
                  <p className="text-sm text-slate-600">{r.desc}</p>
                </div>
              ))}
            </div>
            <h3 className="font-bold text-slate-800 text-lg mb-2">Admin Sidebar Modules:</h3>
            <p className="text-sm text-slate-600 mb-2">The left sidebar shows all available modules based on your permissions:</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {['Dashboard & Analytics', 'Members', 'Rota Management', 'Harvest & Finance', 'Choir Ministry', 'Volunteer Tracking', 'Rules & Tenets', 'Communications', 'Admin Users', 'Audit Logs'].map(m => (
                <div key={m} className="px-3 py-2 bg-slate-100 rounded-lg text-sm text-slate-700 font-medium">{m}</div>
              ))}
            </div>
          </section>

          {/* Section: Members */}
          <section id="members" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <Users className="w-7 h-7 text-indigo-600" /> Members Management
            </h2>
            <SectionImg src={IMG.team} alt="Members Management" caption="View, search, and manage all church members" fallbackIcon={Users} fallbackGradient="from-cyan-500 to-blue-700" />
            <p className="text-slate-700 leading-relaxed mb-4">
              The Members section (accessible in Admin sidebar) shows all registered church members with powerful search and management tools.
            </p>
            <h3 className="font-bold text-slate-800 text-lg mb-2">Key Features:</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1 mb-4 text-sm">
              <li><strong>Search</strong> — Instantly search members by name, phone, or member number</li>
              <li><strong>Member Cards</strong> — Click to expand and see full details (phone, email, birthday, GPS, registration date)</li>
              <li><strong>Edit Names</strong> — Main Admin can edit member names inline</li>
              <li><strong>Delete Members</strong> — Main Admin can remove members with confirmation</li>
              <li><strong>Export</strong> — Download member list as CSV</li>
            </ul>
            <h3 className="font-bold text-slate-800 text-lg mb-2">Homepage Submission Approval:</h3>
            <InfoBox type="info">
              Members who register via the <strong>homepage form</strong> are highlighted in <strong className="text-purple-600">purple</strong> with a <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">Website</span> badge and a <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">Pending</span> status.
            </InfoBox>
            <Step number="1" title="Find Pending Member">Look for members highlighted in purple with "Pending" badge, or check the "Pending Approval" stat card.</Step>
            <Step number="2" title="Expand the Card">Click the member card to expand and see their full details.</Step>
            <Step number="3" title="Accept or Decline">Click the green <strong>"Accept"</strong> button to approve, or red <strong>"Decline"</strong> to reject. No notification is sent to the member.</Step>
          </section>

          {/* Section: Rota */}
          <section id="rota" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <Calendar className="w-7 h-7 text-indigo-600" /> Rota Management
            </h2>
            <SectionImg src={IMG.rota} alt="Rota Management" caption="Generate and manage monthly service rotas" fallbackIcon={Calendar} fallbackGradient="from-amber-500 to-orange-700" />
            <p className="text-slate-700 leading-relaxed mb-4">
              The Rota module generates comprehensive monthly service schedules for the church, complete with Bible lessons, minister assignments, and special events.
            </p>
            <h3 className="font-bold text-slate-800 text-lg mb-2">How to Generate a Rota:</h3>
            <Step number="1" title="Select Month & Year">Choose the target month and year from the dropdowns.</Step>
            <Step number="2" title="Click Generate">The system auto-creates entries for all service days (Sunday Morning, Sunday Evening, Midweek) with the correct Bible lessons from the CCC EC schedule.</Step>
            <Step number="3" title="Edit Assignments">Click individual services to assign ministers, hymn leaders, prayer warriors, and other roles.</Step>
            <Step number="4" title="Export PDF">Click "Export PDF" to download a clean, properly formatted A4 or A3 PDF for printing and distribution.</Step>
            <SectionImg src={IMG.planning} alt="Rota Planning" caption="Organising monthly church service schedules" fallbackIcon={Calendar} fallbackGradient="from-amber-600 to-rose-700" />
            <InfoBox type="info"><strong>Holy Week:</strong> Special Holy Week events (e.g., Palm Sunday, Good Friday) are automatically added for April with auto-expiry dates.</InfoBox>
            <InfoBox type="tip"><strong>Bible Lessons:</strong> Lessons are synced with the official CCC EC website schedule. From May onwards, lessons are manually maintained to avoid scraping issues.</InfoBox>
            <h3 className="font-bold text-slate-800 text-lg mb-2 mt-4">Email Ministers & Automated Reminders:</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1 mb-4 text-sm">
              <li><strong>Email Ministers</strong> — one click sends every minister their personal assignment email plus the full-month rota PDF.</li>
              <li><strong>3-day reminders</strong> — the scheduler emails each assigned minister 3 days before their service, with a link to confirm or change availability.</li>
              <li><strong>Missing-email filter</strong> — in the Ministers Directory, the amber "No email" pill shows only ministers without an email; use "Copy names" to export them for follow-up.</li>
              <li><strong>Availability warnings</strong> — if you try to assign a minister who has marked themselves unavailable, their name shows a red <em>UNAVAILABLE</em> badge in the rota UI.</li>
            </ul>
            <h3 className="font-bold text-slate-800 text-lg mb-2">A3 PDF Auto-Scaling:</h3>
            <p className="text-sm text-slate-600 mb-4">When the monthly rota has lots of entries, fonts and row padding scale down automatically so everything still fits on a single A3 page — perfect for the church noticeboard.</p>
          </section>

          {/* Section: Minister Availability */}
          <section id="availability" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <Calendar className="w-7 h-7 text-indigo-600" /> Minister Availability Calendar
            </h2>
            <InfoBox type="url"><strong>Public URL:</strong> <span className="font-mono">{PRODUCTION_URL}/minister-availability</span></InfoBox>
            <p className="text-slate-700 leading-relaxed mb-4">
              Ministers mark themselves unavailable on specific dates without needing an admin. The rota module uses this to prevent accidental assignments and to surface conflicts visually.
            </p>
            <h3 className="font-bold text-slate-800 text-lg mb-2">How it works:</h3>
            <Step number="1" title="Minister opens the calendar">Via the link in any rota email, or directly at <span className="font-mono text-xs">/minister-availability</span>.</Step>
            <Step number="2" title="They look up their record">The page validates the minister's name/phone.</Step>
            <Step number="3" title="They toggle unavailable dates">Each unavailable date carries a <strong>7-day lockout</strong> — once saved, it can't be flipped back for a week. This prevents last-minute changes disrupting the rota.</Step>
            <Step number="4" title="Rota UI respects the calendar">Any minister flagged unavailable on a service date shows a red <em>UNAVAILABLE</em> badge when an admin tries to assign them.</Step>
            <InfoBox type="tip"><strong>Admin override:</strong> The 7-day lockout applies to ministers only. Admins can still override assignments — the UI just makes sure they see the warning first.</InfoBox>
          </section>

          {/* Section: Harvest & Finance */}
          <section id="harvest" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <PoundSterling className="w-7 h-7 text-indigo-600" /> Harvest & Finance
            </h2>
            <SectionImg src={IMG.finance} alt="Harvest Finance" caption="Track contributions, generate receipts, manage disbursements" fallbackIcon={PoundSterling} fallbackGradient="from-emerald-600 to-green-800" />
            <InfoBox type="url">
              <strong>Mobile Recording:</strong> <span className="font-mono">{PRODUCTION_URL}/harvest-record</span> (admin access required)
            </InfoBox>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Harvest & Finance module is the financial backbone of the platform, handling all contribution recording, receipt generation, and project disbursement tracking. All amounts are displayed in British Pounds (£).
            </p>
            <h3 className="font-bold text-slate-800 text-lg mb-2">Recording a Contribution:</h3>
            <Step number="1" title="Select Project">Choose the harvest project type (e.g., Building Fund, Thanksgiving).</Step>
            <Step number="2" title="Enter Details">Fill in the contributor's name, amount (£), and optional notes.</Step>
            <Step number="3" title="Submit">A unique receipt number is generated automatically.</Step>
            <Step number="4" title="Generate Receipt">Download or print the contribution receipt as PDF or image.</Step>
            <SectionImg src={IMG.receipt} alt="Receipt Generation" caption="Receipts can be downloaded as PDF or image" fallbackIcon={FileText} fallbackGradient="from-emerald-700 to-teal-900" />
            <h3 className="font-bold text-slate-800 text-lg mb-2">Voiding a Contribution:</h3>
            <InfoBox type="warning"><strong>Main Admins Only:</strong> Voiding a contribution requires a mandatory reason. Voided contributions are excluded from all totals and analytics.</InfoBox>
            <Step number="1" title="Find Contribution">Locate the contribution in the harvest list.</Step>
            <Step number="2" title="Click Void">Click the void button and enter a reason for voiding.</Step>
            <Step number="3" title="Confirm">The contribution is marked as voided and removed from all totals.</Step>
            <h3 className="font-bold text-slate-800 text-lg mb-2">Disbursements:</h3>
            <p className="text-sm text-slate-600 mb-4">Track money spent on church projects with disbursement entries. Each entry records the amount, description, and project it's allocated against.</p>
          </section>

          {/* Section: Choir */}
          <section id="choir" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <Music className="w-7 h-7 text-indigo-600" /> Choir Ministry
            </h2>
            <SectionImg src={IMG.choir} alt="Choir Ministry" caption="Managing the church choir — members, songs, and applications" fallbackIcon={Music} fallbackGradient="from-purple-600 to-pink-700" />
            <InfoBox type="url">
              <strong>Join Choir Form:</strong> <span className="font-mono">{PRODUCTION_URL}/join-choir</span> — Share this link for new choir applicants
            </InfoBox>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Choir Ministry module manages choir members, rehearsals, song library, hymn selections, and the digital VOB Choir Membership Form.
            </p>
            <h3 className="font-bold text-slate-800 text-lg mb-2">VOB Choir Membership Form:</h3>
            <p className="text-sm text-slate-600 mb-4">The full digital version of the physical VOB Choir form is available at <span className="font-mono text-xs bg-slate-100 px-1 rounded">{PRODUCTION_URL}/join-choir</span>. It has 6 sections:</p>
            <div className="grid sm:grid-cols-2 gap-2 mb-4">
              {[
                'A — Department (Main Choir, Sunday School, Choreography)',
                'B — Bio Data (Name, Birthday, Gender, Marital Status, Occupation, Phone)',
                'C — Church & Choir Experience',
                'D — Musical Skills (Voice parts, Instruments)',
                'E — References (Next of Kin, Elder)',
                'F — Attestation (Pledge agreement)',
              ].map(s => (
                <div key={s} className="px-3 py-2 bg-purple-50 rounded-lg text-sm text-purple-700 border border-purple-100">{s}</div>
              ))}
            </div>
            <SectionImg src={IMG.music} alt="Choir Performance" caption="VOB Choir — Making a joyful noise unto the Lord" fallbackIcon={Music} fallbackGradient="from-fuchsia-600 to-purple-800" />
            <h3 className="font-bold text-slate-800 text-lg mb-2">Reviewing Choir Applications (Admin):</h3>
            <Step number="1" title="Navigate to Choir Ministry">In the admin panel, click "Choir Ministry" in the sidebar.</Step>
            <Step number="2" title="Open VOB Form Applications tab">Click the "VOB Form Applications" tab to see all submissions.</Step>
            <Step number="3" title="Review & Decide">Pending applications show full details. Click <strong>"Accept"</strong> or <strong>"Decline"</strong> for each.</Step>
            <h3 className="font-bold text-slate-800 text-lg mb-2 mt-4">Other Choir Features:</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1 mb-4 text-sm">
              <li><strong>Song Library</strong> — Add, edit, and organise church songs</li>
              <li><strong>Hymn Selection</strong> — Select hymns for upcoming services</li>
              <li><strong>Rehearsal Scheduling</strong> — Plan and track choir rehearsals</li>
              <li><strong>Email</strong> — Send targeted emails to choir members</li>
              <li><strong>Compulsory fields</strong> — The Join Choir form marks every required field with a red asterisk and is server-side validated. No partial submissions get through.</li>
              <li><strong>Master Password (Main Admin only)</strong> — Set ONE password for every approved choir member. Rotated monthly; every choir member is emailed the new password when it changes.</li>
            </ul>
          </section>

          {/* Section: Choir Check-in (GPS + QR) */}
          <section id="choir-checkin" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <QrCode className="w-7 h-7 text-indigo-600" /> Choir Check-in (GPS + QR)
            </h2>
            <InfoBox type="url"><strong>Public URL:</strong> <span className="font-mono">{PRODUCTION_URL}/choir-checkin</span></InfoBox>
            <p className="text-slate-700 leading-relaxed mb-4">
              Choir members self-record attendance at rehearsals and services. The platform uses a hybrid approach: a GPS fence for direct links, and a physical on-site QR code with a short-lived venue token for fast in-person check-in.
            </p>
            <h3 className="font-bold text-slate-800 text-lg mb-2">Two ways to check in:</h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <h4 className="font-bold text-slate-800 flex items-center gap-2 mb-2"><Globe className="w-4 h-4 text-blue-500" /> Direct Link (GPS-validated)</h4>
                <p className="text-sm text-slate-600 mb-2">A member opens <span className="font-mono text-xs">/choir-checkin</span> directly. Their device GPS is checked against the church coordinates (100m radius). Outside the radius → blocked.</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <h4 className="font-bold text-slate-800 flex items-center gap-2 mb-2"><QrCode className="w-4 h-4 text-emerald-500" /> On-Site QR (Venue Token)</h4>
                <p className="text-sm text-slate-600 mb-2">Admin displays the Choir Check-in QR inside the church. Scanning it adds a short-lived <code>?venue=…</code> token that <strong>bypasses GPS</strong> — ideal when GPS accuracy is poor indoors.</p>
              </div>
            </div>
            <h3 className="font-bold text-slate-800 text-lg mb-2">Member flow:</h3>
            <Step number="1" title="Open page">Direct link or QR scan.</Step>
            <Step number="2" title="Log in">Phone + Password.</Step>
            <Step number="3" title="Confirm the event">The page shows the upcoming rehearsal/service in Europe/London time (no more "No event today" near midnight).</Step>
            <Step number="4" title="Check in">One tap. Location metadata is stored as <code>gps</code>, <code>venue_token</code>, or <code>manual</code> so admins can tell real-world signals apart in the attendance log.</Step>
            <InfoBox type="warning"><strong>Auto absence warnings:</strong> Choir members who miss 2 consecutive events get an automatic "We missed you" email through the scheduler.</InfoBox>
          </section>

          {/* Section: Volunteers */}
          <section id="volunteers" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <Clock className="w-7 h-7 text-indigo-600" /> Volunteer Tracking
            </h2>
            <SectionImg src={IMG.volunteer} alt="Volunteer Tracking" caption="QR-based clock-in/out system for volunteers" fallbackIcon={Clock} fallbackGradient="from-sky-500 to-blue-700" />
            <InfoBox type="url">
              <strong>Volunteer Clock-in:</strong> <span className="font-mono">{PRODUCTION_URL}/volunteer-clockin</span>
            </InfoBox>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Volunteer Tracking system uses QR codes to let volunteers clock in and out of church service sessions. It automatically tracks hours and sends reminders.
            </p>
            <h3 className="font-bold text-slate-800 text-lg mb-2">How It Works:</h3>
            <Step number="1" title="Display QR Code">Print or display the volunteer clock-in QR code at the church entrance.</Step>
            <Step number="2" title="Volunteer Scans">Volunteers scan the QR with their phone and enter their name to clock in.</Step>
            <Step number="3" title="Clock Out">At the end of service, they scan again to clock out. Session hours are recorded automatically.</Step>
            <Step number="4" title="Forgot to Clock Out?">The system sends automated daily email reminders for volunteers who forgot to sign out.</Step>
            <SectionImg src={IMG.qr} alt="QR Scanning" caption="Volunteers scan QR codes with their smartphones" fallbackIcon={QrCode} fallbackGradient="from-indigo-600 to-blue-900" />
            <h3 className="font-bold text-slate-800 text-lg mb-2">Admin View:</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1 text-sm">
              <li>View all active volunteer sessions in real-time</li>
              <li>See historical session logs with hours worked</li>
              <li>Download volunteer reports as CSV</li>
              <li>Manually close open sessions if needed</li>
            </ul>
          </section>

          {/* Section: Rules & Tenets */}
          <section id="rules" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <BookOpen className="w-7 h-7 text-indigo-600" /> Rules & Tenets
            </h2>
            <SectionImg src={IMG.bible} alt="Rules & Tenets" caption="Church rules displayed as full-screen presentation slides" fallbackIcon={BookOpen} fallbackGradient="from-amber-700 to-orange-900" />
            <InfoBox type="url">
              <strong>Presentation:</strong> <span className="font-mono">{PRODUCTION_URL}/rules-presentation</span>
            </InfoBox>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Rules & Tenets module allows you to manage church rules and display them as a professional full-screen presentation during services.
            </p>
            <h3 className="font-bold text-slate-800 text-lg mb-2">Managing Rules (Admin):</h3>
            <Step number="1" title="Add Rules">In the admin panel, go to "Rules & Tenets" and click "Add Rule". Fill in the title, content, section, and optional Bible passage.</Step>
            <Step number="2" title="Organise Sections">Rules are grouped by section (e.g., General, Worship Practice, Dress Code). <strong>Drag-and-drop</strong> the section headings to reorder them — the new order is reflected instantly on the projected presentation.</Step>
            <Step number="3" title="Set Background Images">Optionally add background images and Bible texts to make slides visually appealing.</Step>
            <Step number="4" title="Launch Presentation">Open <span className="font-mono text-xs bg-slate-100 px-1 rounded">{PRODUCTION_URL}/rules-presentation</span> on the church projector.</Step>
            <InfoBox type="info"><strong>Weekly Schedule Slides:</strong> Each service day (Sunday, Wednesday, Friday, etc.) is rendered on its own full-screen slide — no more crammed grids on the projector.</InfoBox>
            <SectionImg src={IMG.pres} alt="Presentation Screen" caption="Project rules on the church screen during services" fallbackIcon={Play} fallbackGradient="from-violet-700 to-fuchsia-900" />
            <h3 className="font-bold text-slate-800 text-lg mb-2">Presentation Controls:</h3>
            <div className="grid sm:grid-cols-2 gap-2 mb-4">
              {[
                { icon: ChevronRight, label: 'Arrow keys or click to navigate slides' },
                { icon: Play, label: 'Auto-play mode with configurable timing' },
                { icon: ArrowUp, label: 'Section order matches admin configuration' },
                { icon: Eye, label: 'Full-screen mode for projectors' },
              ].map((c, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg text-sm text-slate-600">
                  <c.icon className="w-4 h-4 text-indigo-500 shrink-0" />
                  {c.label}
                </div>
              ))}
            </div>
          </section>

          {/* Section: Communications */}
          <section id="email" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <Mail className="w-7 h-7 text-indigo-600" /> Communications
            </h2>
            <SectionImg src={IMG.email} alt="Email Communications" caption="Branded email communications with VOB Church logo" fallbackIcon={Mail} fallbackGradient="from-blue-600 to-cyan-800" />
            <p className="text-slate-700 leading-relaxed mb-4">
              The platform includes a built-in email system for sending targeted communications to church members. All emails are branded with the VOB Church logo.
            </p>
            <h3 className="font-bold text-slate-800 text-lg mb-2">Email Features:</h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1 text-sm mb-4">
              <li><strong>Department Emails</strong> — Send to specific departments or ministries</li>
              <li><strong>Bulk Emails</strong> — Send to all members at once</li>
              <li><strong>Automated Reminders</strong> — Volunteer sign-out reminders sent daily</li>
              <li><strong>Void Notifications</strong> — Contributors are notified when a contribution is voided</li>
              <li><strong>Branded Templates</strong> — All emails include the VOB Church logo and consistent styling</li>
            </ul>
          </section>

          {/* Section: Analytics */}
          <section id="analytics" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <BarChart3 className="w-7 h-7 text-indigo-600" /> Analytics & Reports
            </h2>
            <SectionImg src={IMG.dashboard} alt="Analytics Dashboard" caption="Real-time analytics showing attendance and finance trends" fallbackIcon={BarChart3} fallbackGradient="from-indigo-700 to-purple-900" />
            <p className="text-slate-700 leading-relaxed mb-4">
              The Analytics Dashboard provides real-time insights into church operations with visual charts and key metrics.
            </p>
            <h3 className="font-bold text-slate-800 text-lg mb-2">What You Can See:</h3>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {[
                { label: 'Member Growth', desc: 'Track new registrations over time' },
                { label: 'Attendance Trends', desc: 'Sunday attendance patterns' },
                { label: 'Financial Summary', desc: 'Total contributions by project (in £)' },
                { label: 'Volunteer Hours', desc: 'Hours logged by volunteers' },
                { label: 'Harvest Totals', desc: 'Per-project contribution totals' },
                { label: 'Disbursement Reports', desc: 'Track project spending' },
              ].map(a => (
                <div key={a.label} className="p-3 bg-white rounded-xl border border-slate-100">
                  <p className="font-semibold text-slate-800 text-sm">{a.label}</p>
                  <p className="text-xs text-slate-500">{a.desc}</p>
                </div>
              ))}
            </div>
            <InfoBox type="info"><strong>Currency:</strong> All financial data throughout the platform is displayed in British Pounds (£).</InfoBox>
          </section>

          {/* Section: Mobile */}
          <section id="mobile" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <Smartphone className="w-7 h-7 text-indigo-600" /> Mobile Access & QR Codes
            </h2>
            <SectionImg src={IMG.mobile} alt="Mobile Access" caption="Access key features on your mobile phone" fallbackIcon={Smartphone} fallbackGradient="from-teal-500 to-cyan-700" />
            <p className="text-slate-700 leading-relaxed mb-4">
              Several key features are designed as standalone mobile-friendly pages, accessible via QR codes for convenience during church services:
            </p>
            <div className="space-y-3 mb-4">
              {[
                { label: 'Member Registration', url: `${PRODUCTION_URL}/member-register`, desc: 'New members scan to register with GPS capture' },
                { label: 'Harvest Recording', url: `${PRODUCTION_URL}/harvest-record`, desc: 'Admins record contributions on mobile (requires login)' },
                { label: 'Volunteer Clock-in', url: `${PRODUCTION_URL}/volunteer-clockin`, desc: 'Volunteers clock in/out via QR code' },
                { label: 'Join Choir', url: `${PRODUCTION_URL}/join-choir`, desc: 'New choir applicants fill the VOB membership form' },
              ].map(p => (
                <div key={p.label} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200">
                  <QrCode className="w-8 h-8 text-indigo-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-slate-800">{p.label}</p>
                    <p className="text-sm text-slate-600 mb-1">{p.desc}</p>
                    <p className="text-xs font-mono text-indigo-600">{p.url}</p>
                  </div>
                </div>
              ))}
            </div>
            <InfoBox type="tip"><strong>Tip:</strong> Generate QR codes pointing to these production URLs and print them for display at the church entrance, registration desk, and volunteer stations.</InfoBox>
          </section>

          {/* Section: Quick Reference URLs */}
          <section id="urls" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <Globe className="w-7 h-7 text-indigo-600" /> Quick Reference URLs
            </h2>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-indigo-50">
                  <tr>
                    <th className="text-left px-4 py-3 font-bold text-indigo-800">Page</th>
                    <th className="text-left px-4 py-3 font-bold text-indigo-800">URL</th>
                    <th className="text-left px-4 py-3 font-bold text-indigo-800">Access</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    { page: 'Homepage', url: `${PRODUCTION_URL}`, access: 'Public' },
                    { page: 'Admin Login', url: `${PRODUCTION_URL}/login`, access: 'Admin' },
                    { page: 'Member Registration (QR)', url: `${PRODUCTION_URL}/member-register`, access: 'Public' },
                    { page: 'Harvest Recording (Mobile)', url: `${PRODUCTION_URL}/harvest-record`, access: 'Admin' },
                    { page: 'Join Choir Form', url: `${PRODUCTION_URL}/join-choir`, access: 'Public' },
                    { page: 'Choir Check-in (GPS/QR)', url: `${PRODUCTION_URL}/choir-checkin`, access: 'Public' },
                    { page: 'Volunteer Clock-in', url: `${PRODUCTION_URL}/volunteer-clockin`, access: 'Public' },
                    { page: 'Minister Availability', url: `${PRODUCTION_URL}/minister-availability`, access: 'Public' },
                    { page: 'Rules Presentation', url: `${PRODUCTION_URL}/rules-presentation`, access: 'Public' },
                    { page: 'Platform Presentation', url: 'Admin Panel → Secretarial & Admin → Presentation', access: 'Admin' },
                    { page: 'This Handbook', url: 'Admin Panel → Secretarial & Admin → Handbook', access: 'Admin' },
                  ].map(r => (
                    <tr key={r.page} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{r.page}</td>
                      <td className="px-4 py-3 font-mono text-xs text-indigo-600">{r.url}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${r.access === 'Public' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.access}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Footer */}
          <footer className="text-center py-8 border-t border-slate-200">
            <p className="text-sm text-slate-500">{CHURCH_NAME_FULL}</p>
            <p className="text-xs text-slate-400 mt-1">Church Management System &amp; Website Handbook — {versionInfo.version} · {versionInfo.updated}</p>
            <p className="text-xs text-slate-400 mt-1 italic">"For where two or three are gathered together in my name, there am I in the midst of them." — Matthew 18:20</p>
            <div className="mt-4 handbook-noprint">
              <a href="/" className="text-sm text-indigo-600 hover:underline font-medium">Back to Homepage</a>
              <span className="mx-3 text-slate-300">|</span>
              <a href="/platform-presentation" className="text-sm text-indigo-600 hover:underline font-medium">View Presentation</a>
            </div>
            {/* Signature */}
            <div className="mt-8 pt-6 border-t border-slate-200 max-w-md mx-auto">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Built By</p>
              <p className="text-lg font-bold text-indigo-700" style={{ fontFamily: 'cursive' }}>{BUILT_BY}</p>
              <p className="text-xs text-slate-400 mt-1">Designer &amp; Developer · Church Management System</p>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default Handbook;
